/* processed: techdebt-pass 2026-01-25 domain=frontend rule=remove-console-logs */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Send, ChevronDown, ChevronRight, MessageSquare, Bot, X } from 'lucide-react';
import { TypewriterText } from './TypewriterText';
import { AGENTS, AgentId } from '../config/agents';
import { sendAnalyticsEvent } from '../lib/analytics';

export type AgentType = AgentId | 'partner';

export interface AgentChannelState {
    id: AgentType;
    name: string;
    title: string;
    pitch: string;
    isOpen: boolean;
    color: string;
    bgColor: string;
    messages: Message[];
}

export interface Message {
    id: string;
    sender: 'user' | 'agent';
    text: string;
    typingSpeed?: number; // ms per char
    variant?: 'error';
    fromAgent?: AgentType; // originating agent (for cross-channel messages like handoffs)
}

interface GambitSidebarProps {
    initialQuery?: string;
    activeChannels: AgentChannelState[];
    onToggleChannel: (id: AgentType) => void;

    onSendMessage: (id: AgentType, text: string) => void;
    sendLocked?: boolean;
    sendLockMessage?: string;
    onRequestUnlock?: () => void;
    unlockLabel?: string;
    onGambitClick?: () => void;
}

// Generate Styles from Config
const getStyles = (id: AgentType) => {
    // NOTE: Keep these explicit so Tailwind can statically include the classes.
    switch (id) {
        case 'partner':
            return { border: 'border-purple-500', bg: 'bg-purple-500', text: 'text-purple-400', glow: 'shadow-purple-500/20' };
        case 'orchestrator':
            return { border: 'border-cyan-500', bg: 'bg-cyan-500', text: 'text-cyan-400', glow: 'shadow-cyan-500/20' };
        case 'people':
            return { border: 'border-pink-500', bg: 'bg-pink-500', text: 'text-pink-400', glow: 'shadow-pink-500/20' };
        case 'process':
            return { border: 'border-amber-500', bg: 'bg-amber-500', text: 'text-amber-400', glow: 'shadow-amber-500/20' };
        case 'tech':
            return { border: 'border-violet-500', bg: 'bg-violet-500', text: 'text-violet-400', glow: 'shadow-violet-500/20' };
        case 'data':
            return { border: 'border-emerald-500', bg: 'bg-emerald-500', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' };
        default: {
            const agent = AGENTS[id as AgentId];
            return { border: 'border-white/10', bg: agent.bgClass, text: agent.colorClass, glow: 'shadow-white/10' };
        }
    }
};

const useMediaQuery = (query: string) => {
    const getMatches = () => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia(query).matches;
    };

    const [matches, setMatches] = useState(getMatches);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        onChange();

        if (mql.addEventListener) {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }

        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [query]);

    return matches;
};


import { GravityDials } from './GravityDials';

export const GambitSidebar: React.FC<GambitSidebarProps> = ({
    initialQuery,
    activeChannels,
    onToggleChannel,
    onSendMessage,
    sendLocked = false,
    sendLockMessage = 'Chat is locked in manual mode.',
    onRequestUnlock,
    unlockLabel = 'Unlock access',
    onGambitClick
}) => {
    const isMobile = useMediaQuery('(max-width: 767px)');
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [sidebarWidth, setSidebarWidth] = useState(56);
    const [mobileChatId, setMobileChatId] = useState<AgentType | null>(null);
    const mobileChannel = mobileChatId ? activeChannels.find(c => c.id === mobileChatId) : null;

    useEffect(() => {
        if (!isMobile) setMobileChatId(null);
    }, [isMobile]);

    useEffect(() => {
        if (!isMobile) return;
        const sidebarEl = sidebarRef.current;
        if (!sidebarEl) return;

        const measure = () => {
            const nextWidth = Math.round(sidebarEl.getBoundingClientRect().width);
            if (nextWidth > 0) setSidebarWidth(nextWidth);
        };

        measure();
        window.addEventListener('resize', measure);

        let ro: ResizeObserver | null = null;
        if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
            ro = new ResizeObserver(measure);
            ro.observe(sidebarEl);
        }

        return () => {
            window.removeEventListener('resize', measure);
            ro?.disconnect();
        };
    }, [isMobile]);

    return (
        <div
            ref={sidebarRef}
            data-testid="gambit-sidebar"
            className="w-14 max-[420px]:w-12 sm:w-20 md:w-80 flex-shrink-0 h-full flex flex-col border-r border-white/10 bg-black/20 backdrop-blur-xl relative z-20 overflow-x-hidden transition-[width] duration-300"
        >
            <div className="p-2 md:p-4 border-b border-white/5 flex items-center justify-center md:justify-between">
                <h2 data-testid="gambit-sidebar-title" className="hidden md:block text-sm font-bold text-slate-300 tracking-wider uppercase">Crucible Uplink</h2>
                <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse delay-75" />
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse delay-150" />
                </div>
            </div>

            <div className="flex-1 flex flex-col">
                {activeChannels.map(channel => (
                    <AgentAccordion
                        key={channel.id}
                        channel={channel}
                        isMobile={isMobile}
                        isMobileDrawerOpen={mobileChatId === channel.id}
                        onOpenMobileChat={() => setMobileChatId(channel.id)}
                        styles={getStyles(channel.id)}
                        onToggle={() => onToggleChannel(channel.id)}
                        onSend={(text) => onSendMessage(channel.id, text)}
                        sendLocked={sendLocked}
                        sendLockMessage={sendLockMessage}
                        onRequestUnlock={onRequestUnlock}
                        unlockLabel={unlockLabel}
                        onGambitClick={onGambitClick}
                    />
                ))}
            </div>

            {isMobile && mobileChannel && typeof document !== 'undefined'
                ? createPortal(
                    <MobileChatDrawer
                        channel={mobileChannel}
                        railWidth={sidebarWidth}
                        styles={getStyles(mobileChannel.id)}
                        onClose={() => setMobileChatId(null)}
                        onSend={(text) => onSendMessage(mobileChannel.id, text)}
                        sendLocked={sendLocked}
                        sendLockMessage={sendLockMessage}
                        onRequestUnlock={onRequestUnlock}
                        unlockLabel={unlockLabel}
                        onGambitClick={onGambitClick}
                    />,
                    document.body
                )
                : null}

            {/* Strategy Dials Footer */}
            <div className="border-t border-white/10 bg-black/40">
                <GravityDials compact={true} />
            </div>
        </div>
    );
};

const AgentAccordion: React.FC<{
    channel: AgentChannelState;
    isMobile: boolean;
    isMobileDrawerOpen: boolean;
    onOpenMobileChat: () => void;
    styles: { border: string; bg: string; text: string; glow: string };
    onToggle: () => void;
    onSend: (text: string) => void;
    sendLocked: boolean;
    sendLockMessage: string;
    onRequestUnlock?: () => void;
    unlockLabel: string;
    onGambitClick?: () => void;
}> = ({ channel, isMobile, isMobileDrawerOpen, onOpenMobileChat, styles, onToggle, onSend, sendLocked, sendLockMessage, onRequestUnlock, unlockLabel, onGambitClick }) => {
    const [input, setInput] = useState('');
    const bottomRef = useRef<HTMLDivElement>(null);
    const [messageAnimation, setMessageAnimation] = useState<{ [key: string]: boolean }>({});
    const isOpenVisual = channel.isOpen || isMobileDrawerOpen;
    const isExtraNarrow = useMediaQuery('(max-width: 420px)');

    useEffect(() => {
        if (channel.isOpen && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [channel.messages, channel.isOpen]);

    // Animate new messages on entrance
    useEffect(() => {
        channel.messages.forEach(msg => {
            if (!messageAnimation[msg.id]) {
                setTimeout(() => {
                    setMessageAnimation(prev => ({ ...prev, [msg.id]: true }));
                }, 50);
            }
        });
    }, [channel.messages, messageAnimation]);

    const handleSend = () => {
        if (sendLocked) return;
        if (!input.trim()) return;
        onSend(input);
        setInput('');
    };

    // Parse text to highlight bold keywords and clickable Gambit link
    const renderText = (text: string) => {
        const parts = text.split(/(\*[^*]+\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('*') && part.endsWith('*')) {
                const content = part.slice(1, -1);

                // Special handling for Gambit Link
                if (content === 'Gambit') {
                    return (
                        <button
                            key={i}
                            onClick={onGambitClick}
                            className="text-purple-400 font-bold hover:text-purple-300 hover:underline cursor-pointer transition-colors"
                        >
                            {content}
                        </button>
                    );
                }

                let colorClass = styles.text; // Default to sender's color

                // Dynamic Domain Coloring based on Config
                Object.values(AGENTS).forEach(agent => {
                    if (content === agent.id.toUpperCase() || content === agent.name) {
                        colorClass = agent.colorClass;
                    }
                });

                return <span key={i} className={`${colorClass} font-bold`}>{content}</span>;
            }
            return part;
        });
    };

    return (
        <div data-testid={`agent-accordion-${channel.id}`} className="border-b border-white/5 transition-all duration-300 relative group/accordion">
            {/* Elevator Pitch Tooltip (Hover on Header) */}
            <div className="absolute left-14 top-12 z-50 pointer-events-none opacity-0 group-hover/accordion:opacity-0 transition-opacity duration-300">
                {/* Tooltip logic moved to inner header hover or strictly css */}
            </div>

            {/* Header */}
            <button
                onClick={() => (isMobile ? onOpenMobileChat() : onToggle())}
                data-testid={`agent-accordion-toggle-${channel.id}`}
                className={`w-full flex items-center justify-between p-1.5 max-[420px]:p-1 md:p-3 ${isOpenVisual ? 'bg-white/5' : 'hover:bg-white/5'} transition-colors group relative`}
                            style={isOpenVisual ? {
                                boxShadow: `0 0 20px ${AGENTS[channel.id as AgentId]?.colorHex || '#fff'}33`,
                            } : undefined}
            >
                {/* Mobile header (collapsed sidebar) */}
                <div className="md:hidden w-full flex flex-col items-center gap-1 max-[420px]:gap-0.5 -ml-1 max-[420px]:-ml-0.5">
                    <div className="flex items-center gap-1 text-slate-500">
                        {isOpenVisual ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                    <div className="relative">
                        <div className={`w-8 h-8 max-[420px]:w-7 max-[420px]:h-7 rounded-lg flex items-center justify-center ${styles.bg} bg-opacity-20 border border-white/10`}>
                            <Bot className={`w-4 h-4 max-[420px]:w-3.5 max-[420px]:h-3.5 ${styles.text}`} />
                        </div>
                        {channel.messages.length > 0 && (
                            <div className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-white/10 text-[10px] text-slate-300 flex items-center justify-center">
                                {channel.messages.length}
                            </div>
                        )}
                    </div>
                    <div
                        title={channel.name}
                        className={`text-[10px] max-[420px]:text-[11px] font-bold ${styles.text} uppercase tracking-widest`}
                    >
                        {channel.name.slice(0, isExtraNarrow ? 1 : 2)}
                    </div>
                </div>

                {/* Desktop header */}
                <div className="hidden md:flex items-center gap-3">
                    {isOpenVisual ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}

                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${styles.bg} bg-opacity-20 border border-white/10 group-hover:scale-105 transition-transform`}>
                        <Bot className={`w-5 h-5 ${styles.text}`} />
                    </div>

                    <div className="text-left">
                        <div className="flex items-center gap-2">
                            <div className={`text-sm font-bold ${styles.text}`}>{channel.name}</div>
                            {channel.isOpen && <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">{channel.title}</div>}
                        </div>
                        {(() => {
                            const agentCfg = AGENTS[channel.id as AgentId];
                            return agentCfg?.stratum ? (
                                <div className={`text-[9px] font-mono tracking-wide ${agentCfg.stratum.colorClass} opacity-60`}>
                                    {agentCfg.stratum.label}
                                </div>
                            ) : null;
                        })()}
                        {!channel.isOpen && (
                            <div className="text-[10px] text-slate-400 truncate max-w-[180px]">
                                {channel.pitch}
                            </div>
                        )}
                    </div>
                </div>
                {channel.messages.length > 0 && (
                    <div className="hidden md:block px-2 py-0.5 rounded-full bg-white/10 text-[10px] text-slate-400">
                        {channel.messages.length}
                    </div>
                )}
            </button>

            {/* Chat Content */}
            {channel.isOpen && (
                <div data-testid={`agent-accordion-panel-${channel.id}`} className="hidden md:block bg-black/20 animate-fade-in-up">
                    <div className="p-6 space-y-6 max-h-[500px] overflow-y-auto">
                        {channel.messages.length === 0 && (
                            <div className="text-center text-xs text-slate-600 italic py-4">
                                {channel.name} ({channel.title}) is connected.
                            </div>
                        )}
                        {channel.messages.map(msg => {
                            // Use originating agent's color for cross-channel messages (e.g. Nova handoffs)
                            const msgStyles = msg.sender === 'agent' && msg.fromAgent ? getStyles(msg.fromAgent) : styles;
                            const senderAgent = msg.fromAgent ? AGENTS[msg.fromAgent as AgentId] : null;
                            const senderName = msg.sender === 'agent' && senderAgent ? senderAgent.name : channel.name;
                            const senderTitle = msg.sender === 'agent' && senderAgent ? senderAgent.title : channel.title;

                            return (
                            <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'} mb-1`}>
                                {/* Agent Label (Compact) */}
                                {msg.sender === 'agent' && (
                                    <div className="flex items-baseline gap-2 mb-0.5 ml-1">
                                        <span className={`text-[10px] font-bold ${msgStyles.text} uppercase tracking-wider`}>
                                            {senderName}
                                        </span>
                                        <span className="text-[8px] text-slate-500 uppercase tracking-tight font-mono">
                                            {senderTitle}
                                        </span>
                                    </div>
                                )}

                                <div
                                    data-testid={msg.variant === 'error' ? 'chat-message-error' : undefined}
                                    data-message-variant={msg.variant || 'default'}
                                    className={`relative max-w-[90%] rounded-xl px-3 py-1.5 text-xs leading-relaxed shadow-lg ${msg.sender === 'user'
                                        ? `bg-[#1a1f2e] text-slate-200 rounded-br-none border border-white/5`
                                        : `bg-[#0a0f1c] ${msgStyles.border} border border-opacity-40 ${msg.variant === 'error' ? 'text-red-400' : 'text-slate-100'} rounded-bl-none shadow-none`
                                        } transition-opacity duration-500 ${messageAnimation[msg.id]
                                        ? 'opacity-100'
                                        : 'opacity-0'}`}
                                >
                                    {/* Agent Glow Element */}
                                    {msg.sender === 'agent' && (
                                        <div className={`absolute inset-0 rounded-xl opacity-10 ${msgStyles.bg} pointer-events-none`} />
                                    )}
                                    <span className="relative z-10 [font-variant-ligatures:none] [font-kerning:none]">
                                        <TypewriterText
                                            text={msg.text}
                                            speed={msg.typingSpeed ?? 0}
                                            renderText={renderText}
                                        />
                                    </span>
                                </div>
                            </div>
                            );
                        })}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-3 border-t border-white/5 bg-black/40">
                        {sendLocked && (
                            <div
                                data-testid={`agent-accordion-lock-banner-${channel.id}`}
                                className="mb-3 rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100"
                            >
                                <div className="font-semibold">Manual mode active</div>
                                <div className="mt-1 text-amber-100/80">{sendLockMessage}</div>
                                {onRequestUnlock && (
                                    <button
                                        type="button"
                                        onClick={onRequestUnlock}
                                        className="mt-2 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold text-white hover:bg-white/10 transition-colors"
                                    >
                                        {unlockLabel}
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="relative">
                            <input
                                type="text"
                                data-testid={`agent-accordion-input-${channel.id}`}
                                value={input}
                                disabled={sendLocked}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && !sendLocked && handleSend()}
                                placeholder={sendLocked ? sendLockMessage : channel.name === 'Nova' ? "Type your challenge... (e.g., 'My team is overcommitted')" : `Message ${channel.name}...`}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-3 pr-10 text-xs text-white focus:outline-none focus:border-white/20 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                            />
                            <button
                                onClick={handleSend}
                                aria-label="Send message"
                                disabled={sendLocked}
                                className="absolute right-2 top-1.5 text-slate-400 hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <Send className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const MobileChatDrawer: React.FC<{
    channel: AgentChannelState;
    railWidth: number;
    styles: { border: string; bg: string; text: string; glow: string };
    onClose: () => void;
    onSend: (text: string) => void;
    sendLocked: boolean;
    sendLockMessage: string;
    onRequestUnlock?: () => void;
    unlockLabel: string;
    onGambitClick?: () => void;
}> = ({ channel, railWidth, styles, onClose, onSend, sendLocked, sendLockMessage, onRequestUnlock, unlockLabel, onGambitClick }) => {
    const [input, setInput] = useState('');
    const bottomRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const raf = window.requestAnimationFrame(() => setIsVisible(true));
        return () => window.cancelAnimationFrame(raf);
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [channel.messages.length]);

    const handleSend = () => {
        if (sendLocked) return;
        if (!input.trim()) return;
        onSend(input);
        setInput('');
    };

    const renderText = (text: string) => {
        const parts = text.split(/(\*[^*]+\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('*') && part.endsWith('*')) {
                const content = part.slice(1, -1);
                if (content === 'Gambit') {
                    return (
                        <button
                            key={i}
                            onClick={onGambitClick}
                            className="text-purple-400 font-bold hover:text-purple-300 hover:underline cursor-pointer transition-colors"
                        >
                            {content}
                        </button>
                    );
                }

                let colorClass = styles.text;
                Object.values(AGENTS).forEach(agent => {
                    if (content === agent.id.toUpperCase() || content === agent.name) {
                        colorClass = agent.colorClass;
                    }
                });

                return (
                    <span key={i} className={`${colorClass} font-bold`}>
                        {content}
                    </span>
                );
            }
            return part;
        });
    };

    return (
        <div className="fixed inset-0 z-[70] md:hidden pointer-events-none">
            <div
                className={`absolute inset-0 bg-black/70 transition-opacity duration-300 pointer-events-auto ${isVisible ? 'opacity-100' : 'opacity-0'}`}
                style={{ left: `${railWidth}px` }}
                onClick={onClose}
            />
            <div
                className={`
                    absolute inset-y-0 right-0
                    bg-[#050510] border-r border-white/10 backdrop-blur-xl
                    transition-transform duration-300 pointer-events-auto
                    ${isVisible ? 'translate-x-0' : '-translate-x-full'}
                `}
                style={{ left: `${railWidth}px` }}
                role="dialog"
                aria-modal="true"
            >
                <div className="h-full flex flex-col">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/30">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${styles.bg} bg-opacity-20 border border-white/10`}>
                                <Bot className={`w-5 h-5 ${styles.text}`} />
                            </div>
                            <div className="min-w-0">
                                <div className={`text-sm font-bold ${styles.text}`}>{channel.name}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono truncate">{channel.title}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close chat"
                            className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {channel.messages.length === 0 && (
                            <div className="text-center text-xs text-slate-600 italic py-6">
                                {channel.name} ({channel.title}) is connected.
                            </div>
                        )}
                        {channel.messages.map(msg => {
                            const msgStyles = msg.sender === 'agent' && msg.fromAgent ? getStyles(msg.fromAgent) : styles;
                            const senderAgent = msg.fromAgent ? AGENTS[msg.fromAgent as AgentId] : null;
                            const senderName = msg.sender === 'agent' && senderAgent ? senderAgent.name : channel.name;
                            const senderTitle = msg.sender === 'agent' && senderAgent ? senderAgent.title : channel.title;

                            return (
                            <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                                {msg.sender === 'agent' && (
                                    <div className="flex items-baseline gap-2 mb-0.5 ml-1">
                                        <span className={`text-[10px] font-bold ${msgStyles.text} uppercase tracking-wider`}>
                                            {senderName}
                                        </span>
                                        <span className="text-[8px] text-slate-500 uppercase tracking-tight font-mono">
                                            {senderTitle}
                                        </span>
                                    </div>
                                )}

                                <div
                                    data-testid={msg.variant === 'error' ? 'chat-message-error' : undefined}
                                    data-message-variant={msg.variant || 'default'}
                                    className={`relative max-w-[92%] rounded-xl px-3 py-2 text-sm leading-relaxed ${msg.sender === 'user'
                                        ? `bg-[#1a1f2e] text-slate-200 rounded-br-none border border-white/5`
                                        : `bg-[#0a0f1c] ${msgStyles.border} border border-opacity-40 ${msg.variant === 'error' ? 'text-red-400' : 'text-slate-100'} rounded-bl-none shadow-none`
                                        }`}
                                >
                                    {msg.sender === 'agent' && (
                                        <div className={`absolute inset-0 rounded-xl opacity-10 ${msgStyles.bg} pointer-events-none`} />
                                    )}
                                    <span className="relative z-10 [font-variant-ligatures:none] [font-kerning:none]">
                                        <TypewriterText
                                            text={msg.text}
                                            speed={msg.typingSpeed ?? 0}
                                            renderText={renderText}
                                        />
                                    </span>
                                </div>
                            </div>
                            );
                        })}
                        <div ref={bottomRef} />
                    </div>

                    <div className="p-4 border-t border-white/10 bg-black/40">
                        {sendLocked && (
                            <div className="mb-3 rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                                <div className="font-semibold">Manual mode active</div>
                                <div className="mt-1 text-amber-100/80">{sendLockMessage}</div>
                                {onRequestUnlock && (
                                    <button
                                        type="button"
                                        onClick={onRequestUnlock}
                                        className="mt-2 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold text-white hover:bg-white/10 transition-colors"
                                    >
                                        {unlockLabel}
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="relative">
                            <input
                                type="text"
                                value={input}
                                disabled={sendLocked}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && !sendLocked && handleSend()}
                                placeholder={sendLocked ? sendLockMessage : channel.name === 'Nova' ? "Type your challenge... (e.g., 'My team is overcommitted')" : `Message ${channel.name}...`}
                                className="w-full min-h-[44px] bg-white/5 border border-white/10 rounded-lg py-2.5 pl-3 pr-12 text-sm text-white focus:outline-none focus:border-white/20 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                            />
                            <button
                                type="button"
                                onClick={handleSend}
                                aria-label="Send message"
                                disabled={sendLocked}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
